import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const OperationsFilters = ({
  filters,
  operations,
  onFilterChange,
  onClearFilters,
}) => {
  const { t } = useTranslation();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const hasActiveFilters = Object.keys(filters).some(key => {
    const value = filters[key];
    return value !== undefined && value !== '' && value !== null;
  });

  const uniqueFloors = [...new Set(operations.map(op => op.unitCode.charAt(0)))].sort();

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100 mb-4 sm:mb-6">
      <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">
        {t('filtersAndSearch')}
      </h3>
      
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search size={18} className="absolute start-3 top-1/2 transform -translate-y-1/2 text-slate-400 " />
          <input
            type="text"
            placeholder={t('searchOperations')}
            value={filters.searchTerm || ''}
            onChange={(e) => onFilterChange('searchTerm', e.target.value)}
            className="w-full ps-10 pe-6 py-2 sm:py-3 border border-slate-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
          />
        </div>
      </div>

      {/* Quick Status Filters */}
      <div className="mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          <button
            onClick={() => onFilterChange('status', filters.status === 'pending' ? undefined : 'pending')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
              filters.status === 'pending'
                ? 'bg-yellow-600 text-white shadow-md'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            {t('statusPending')}
          </button>
          <button
            onClick={() => onFilterChange('status', filters.status === 'confirmed' ? undefined : 'confirmed')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
              filters.status === 'confirmed'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {t('statusConfirmed')}
          </button>
          <button
            onClick={() => onFilterChange('status', filters.status === 'early' ? undefined : 'early')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
              filters.status === 'early'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {t('statusEarly')}
          </button>
          <button
            onClick={() => onFilterChange('status', filters.status === 'late' ? undefined : 'late')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
              filters.status === 'late'
                ? 'bg-orange-600 text-white shadow-md'
                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
            }`}
          >
            {t('statusLate')}
          </button>
          <button
            onClick={() => onFilterChange('status', filters.status === 'noShow' ? undefined : 'noShow')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
              filters.status === 'noShow'
                ? 'bg-red-600 text-white shadow-md'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {t('statusNoShow')}
          </button>
          <button
            onClick={() => onFilterChange('priority', filters.priority === 'urgent' ? undefined : 'urgent')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
              filters.priority === 'urgent'
                ? 'bg-red-600 text-white shadow-md'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {t('priorityUrgent')}
          </button>
        </div>
      </div>

      {/* Advanced Filters Toggle */}
      <div className="flex items-center justify-between">
        {/* <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className={`flex items-center space-x-2 rtl:space-x-reverse px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200 ${
            showAdvancedFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''
          }`}
        >
          <Filter size={16} />
          <span>{showAdvancedFilters ? t('operationsHideFilters') : t('operationsMoreFilters')}</span>
        </button> */}
        <div></div>

        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center space-x-2 rtl:space-x-reverse px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
          >
            <X size={16} />
            <span>{t('operationsClearFilters')}</span>
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('filterByType')}
              </label>
              <select
                value={filters.type || ''}
                onChange={(e) => onFilterChange('type', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('allOperations')}</option>
                <option value="checkin">{t('checkInOperations')}</option>
                <option value="checkout">{t('checkOutOperations')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('filterByFloor')}
              </label>
              <select
                value={filters.floor || ''}
                onChange={(e) => onFilterChange('floor', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('allFloors')}</option>
                {uniqueFloors.map(floor => (
                  <option key={floor} value={floor}>
                    {t('floor')} {floor}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('filterByRoomStatus')}
              </label>
              <select
                value={filters.roomStatus || ''}
                onChange={(e) => onFilterChange('roomStatus', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('allRoomStatuses')}</option>
                <option value="clean">{t('roomStatusClean')}</option>
                <option value="dirty">{t('roomStatusDirty')}</option>
                <option value="cleaning">{t('roomStatusCleaning')}</option>
                <option value="maintenance">{t('roomStatusMaintenance')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('filterByTimeSlot')}
              </label>
              <select
                value={filters.timeSlot || ''}
                onChange={(e) => onFilterChange('timeSlot', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('allTimeSlots')}</option>
                <option value="morning">{t('timeSlotMorning')}</option>
                <option value="afternoon">{t('timeSlotAfternoon')}</option>
                <option value="evening">{t('timeSlotEvening')}</option>
                <option value="late">{t('timeSlotLate')}</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};